import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { VehiclesService } from 'src/app/shared/api-services/vehicles.service';
import { CreateTaskRequest } from 'src/app/shared/models/task/task-request';
import { Vehicle } from 'src/app/shared/models/vehicle/vehicle';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'task-end-mileage',
    imports: [
      CommonModule,
      TranslateModule,
      ReactiveFormsModule,
    ],
    template: `
      <form [formGroup]="form">
        <div class="form-group">
            <label class="form-label" for="mileage">{{'TASKS.MODAL_EDIT_TASK.INSERT_MILEAGE' | translate: {x: vehicle?.mileage || 0} }}</label>
            <div class="input-group">
                <input 
                  type="number" 
                  class="form-control" 
                  [class.is-invalid]="form.get('mileage')?.invalid && form.get('mileage')?.touched"
                  formControlName="mileage" />
                <span class="input-group-text">{{'TASKS.MODAL_EDIT_TASK.MILEAGE' | translate}}</span>
            </div>
            @if (form.get('mileage')?.invalid && form.get('mileage')?.touched) {
              <div class="form-text invalid-feedback d-block">
                {{'TASKS.MODAL_EDIT_TASK.MILEAGE_ERROR_MIN' | translate: {minMileage: vehicle?.mileage} }}
              </div>
            }
            <span class="form-text">{{'TASKS.MODAL_EDIT_TASK.MILEAGE_INFO' | translate}}</span>
        </div>
      </form>
    `,
})
export class TaskEndMileageComponent implements OnInit, OnChanges, OnDestroy {  
  @Input() taskRequest: CreateTaskRequest;
  @Output() onMileageChange = new EventEmitter<number>();
  
  form: FormGroup;
  vehicle: Vehicle;
  private _destroy$: Subject<void> = new Subject<void>();
  
  constructor(
    private _vehiclesService: VehiclesService,
  ) {
    this.form = new FormGroup({
      mileage: new FormControl(null),
    });
  }

  ngOnInit(): void {
    this.form.patchValue({
      mileage: this.taskRequest.endMileage,
    });

    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.onMileageChange.emit(value.mileage);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // in taskRequest vehicleId change, update the form
    if (changes['taskRequest'] && this.taskRequest.vehicleId) {
      this._vehiclesService.getVehicleById$(this.taskRequest.vehicleId)
      .pipe(takeUntil(this._destroy$)).subscribe((vehicle) => {
        this.vehicle = vehicle;
        this.updateMileageValidators();
      });
    }
  }

  private updateMileageValidators(): void {
    if (this.vehicle?.mileage !== undefined) {
      const mileageControl = this.form.get('mileage');
      mileageControl?.setValidators([
        this.minMileageValidator(this.vehicle.mileage)
      ]);
      mileageControl?.updateValueAndValidity();
    }
  }

  private minMileageValidator(minMileage: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }
      
      const value = Number(control.value);
      if (value < minMileage) {
        return { minMileage: { actualValue: value, requiredMinimum: minMileage } };
      }
      
      return null;
    };
  }
  
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

}
